import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table, Typography, Row, Col, Space, Select, Button, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Text } = Typography;

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

export type Institucion = {
  id: number;
  nombre: string;
  siglas?: string;
};

export type Mesa = {
  id: number;
  nombre: string;
  siglas?: string;
  grupo_mesa_abreviatura: string
};

export type Coe = {
  id: number;
  nombre: string;
  siglas?: string;
};

export type LocationOption = {
  id: number;
  nombre: string;
};

export type RecursoTipoRow = {
  recurso_tipo_id: number;
  recurso_tipo_nombre: string;
};

type RecursoSelectorOption = {
  label: string;
  value: string;
  grupoId: number;
  tipoNombre?: string;
};

export type InventarioCellPayload = {
  existencias?: number | null;
  inventario_disponible?: number | null;
  id?: number;
  existencias_detalle?: Array<{
    parroquia_id: number;
    parroquia_nombre?: string;
    canton_id?: number;
    provincia_id?: number;
    existencias: number;
  }>;
};

export interface InventarioMatrixProps {
  tableTitle: string;
  coes?: Coe[];
  coesStatus?: 'idle' | 'loading' | 'succeeded' | 'failed';
  selectedCoeId?: number;
  onCoeChange?: (coeId: number) => void;
  showCoeSelector?: boolean;
  provincias?: LocationOption[];
  provinciasStatus?: 'idle' | 'loading' | 'succeeded' | 'failed';
  selectedProvinciaId?: number;
  onProvinciaChange?: (provinciaId: number) => void;
  cantones?: LocationOption[];
  cantonesStatus?: 'idle' | 'loading' | 'succeeded' | 'failed';
  selectedCantonId?: number;
  onCantonChange?: (cantonId: number) => void;
  showLocationSelectors?: boolean;
  mesas: Mesa[];
  mesasStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  selectedMesaId?: number;
  onMesaChange: (mesaId: number) => void;
  hideMesaSelector?: boolean;
  recursoGrupos: Array<{ id: number; nombre: string; searchText?: string; tipoNombres?: string[] }>;
  recursoGruposStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  selectedGrupoId?: number;
  onGrupoChange: (grupoId: number) => void;
  onLoadMatrix: () => void;
  rows: RecursoTipoRow[];
  instituciones: Institucion[];
  getCell: (recursoTipoId: number, institucionId: number) => InventarioCellPayload | undefined;
  onCellClick: (row: RecursoTipoRow, institucion: Institucion) => void;
  selectedRowId?: number | null;
  selectedInstitucionId?: number | null;
  loading?: boolean;
  loadDisabled?: boolean;
}

export const InventarioMatrix: React.FC<InventarioMatrixProps> = ({
  tableTitle,
  coes = [],
  coesStatus = 'idle',
  selectedCoeId,
  onCoeChange,
  showCoeSelector = false,
  provincias = [],
  provinciasStatus = 'idle',
  selectedProvinciaId,
  onProvinciaChange,
  cantones = [],
  cantonesStatus = 'idle',
  selectedCantonId,
  onCantonChange,
  showLocationSelectors = false,
  mesas,
  mesasStatus,
  selectedMesaId,
  onMesaChange,
  hideMesaSelector = false,
  recursoGrupos,
  recursoGruposStatus,
  selectedGrupoId,
  onGrupoChange,
  onLoadMatrix,
  rows,
  instituciones,
  getCell,
  onCellClick,
  selectedRowId,
  selectedInstitucionId,
  loading = false,
  loadDisabled = false,
}) => {
  const [grupoSearchText, setGrupoSearchText] = useState('');
  const [targetRecursoTipo, setTargetRecursoTipo] = useState('');
  const [selectedRecursoValue, setSelectedRecursoValue] = useState<string | undefined>(
    selectedGrupoId ? `grupo:${selectedGrupoId}` : undefined
  );
  const [highlightedRowId, setHighlightedRowId] = useState<number | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const selectedGrupoRef = useRef(selectedGrupoId);
  const pendingScrollRef = useRef(false);
  const loadStartedRef = useRef(false);

  const recursoSelectorOptions = useMemo<RecursoSelectorOption[]>(() => {
    const normalizedSearch = normalizeSearchText(grupoSearchText);

    if (!normalizedSearch) {
      const groupOptions = recursoGrupos.map((group) => ({
        label: group.nombre,
        value: `grupo:${group.id}`,
        grupoId: group.id,
      }));
      const selectedGroup = recursoGrupos.find((group) => group.id === selectedGrupoId);

      return targetRecursoTipo && selectedGroup
        ? [
            {
              label: `${selectedGroup.nombre} - ${targetRecursoTipo}`,
              value: `tipo:${selectedGroup.id}:${targetRecursoTipo}`,
              grupoId: selectedGroup.id,
              tipoNombre: targetRecursoTipo,
            },
            ...groupOptions,
          ]
        : groupOptions;
    }

    return recursoGrupos.flatMap((group) => {
      const groupMatches = normalizeSearchText(group.nombre).includes(normalizedSearch);
      const matchingTypes = (group.tipoNombres ?? []).filter(
        (typeName) =>
          groupMatches || normalizeSearchText(typeName).includes(normalizedSearch)
      );
      const typeOptions = matchingTypes.map((typeName) => ({
        label: `${group.nombre} - ${typeName}`,
        value: `tipo:${group.id}:${typeName}`,
        grupoId: group.id,
        tipoNombre: typeName,
      }));

      return groupMatches
        ? [
            {
              label: group.nombre,
              value: `grupo:${group.id}`,
              grupoId: group.id,
            },
            ...typeOptions,
          ]
        : typeOptions;
    });
  }, [grupoSearchText, recursoGrupos, selectedGrupoId, targetRecursoTipo]);

  useEffect(() => {
    if (selectedGrupoRef.current === selectedGrupoId) return;

    selectedGrupoRef.current = selectedGrupoId;
    setSelectedRecursoValue(selectedGrupoId ? `grupo:${selectedGrupoId}` : undefined);
    setTargetRecursoTipo('');
    setHighlightedRowId(null);
  }, [selectedGrupoId]);

  useEffect(() => {
    if (pendingScrollRef.current && loading) {
      loadStartedRef.current = true;
      return;
    }

    if (!pendingScrollRef.current || !loadStartedRef.current || loading) return;

    pendingScrollRef.current = false;
    loadStartedRef.current = false;

    const normalizedTarget = normalizeSearchText(targetRecursoTipo);
    if (!normalizedTarget) return;

    const targetRow = rows.find(
      (row) => normalizeSearchText(row.recurso_tipo_nombre) === normalizedTarget
    );
    if (!targetRow) return;

    setHighlightedRowId(targetRow.recurso_tipo_id);

    requestAnimationFrame(() => {
      const rowElement = tableContainerRef.current?.querySelector<HTMLElement>(
        `[data-row-key="${targetRow.recurso_tipo_id}"]`
      );
      rowElement?.focus({ preventScroll: true });
      rowElement?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    });
  }, [loading, rows, targetRecursoTipo]);

  const handleGrupoChange = (value: string) => {
    const selectedOption = recursoSelectorOptions.find((option) => option.value === value);
    if (!selectedOption) return;

    selectedGrupoRef.current = selectedOption.grupoId;
    setSelectedRecursoValue(value);
    setTargetRecursoTipo(selectedOption.tipoNombre ?? '');
    setHighlightedRowId(null);
    onGrupoChange(selectedOption.grupoId);
  };

  const handleLoadMatrix = () => {
    pendingScrollRef.current = Boolean(targetRecursoTipo);
    loadStartedRef.current = false;
    setHighlightedRowId(null);
    onLoadMatrix();
  };

  const columns: ColumnsType<RecursoTipoRow> = useMemo(() => {
    const base: ColumnsType<RecursoTipoRow> = [
      {
        title: 'Tipo Recurso',
        key: 'tipo',
        fixed: 'left',
        width: 260,
        render: (_, row) => <Text strong>{row.recurso_tipo_nombre}</Text>,
      },
    ];

    const dynamic: ColumnsType<RecursoTipoRow> = instituciones.map((inst) => ({
      title: inst.siglas ? `${inst.siglas} - ${inst.nombre}` : inst.nombre,
      key: `inst_${inst.id}`,
      width: 220,
      render: (_, row) => {
        const cell = getCell(row.recurso_tipo_id, inst.id);
        const existencias = Number(cell?.existencias ?? 0);
        const isSelected = selectedRowId === row.recurso_tipo_id && selectedInstitucionId === inst.id;
        return (
          <button
            type="button"
            onClick={() => onCellClick(row, inst)}
            data-tour="inventario-celda-existencia"
            style={{
              width: '100%',
              border: isSelected ? '2px solid #0ea5e9' : '1px solid #d9d9d9',
              borderRadius: 8,
              background: isSelected ? '#e0f2fe' : '#fff',
              boxShadow: isSelected ? '0 0 0 3px rgba(14,165,233,.25)' : 'none',
              padding: '8px 10px',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <Text strong>{existencias}</Text>
            </div>
            
          </button>
        );
      },
    }));

    return [...base, ...dynamic];
  }, [instituciones, getCell, onCellClick, selectedInstitucionId, selectedRowId]);

  return (
    <div data-tour="inventario-matriz-panel">
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: 22 }}>{tableTitle}</Text>
      </div>

      <Row gutter={[12, 12]} style={{ marginBottom: 8 }}>
        {showCoeSelector && (
          <Col xs={24} md={8} lg={4}>
            <div data-tour="inventario-coe">
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text strong>COE</Text>
                <Select
                  placeholder="Seleccione COE"
                  options={coes.map((coe) => ({
                    label: coe.siglas ? `${coe.siglas} - ${coe.nombre}` : coe.nombre,
                    value: coe.id,
                  }))}
                  value={selectedCoeId}
                  onChange={(value) => onCoeChange?.(value)}
                  disabled={coesStatus === 'loading'}
                  loading={coesStatus === 'loading'}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                />
              </Space>
            </div>
          </Col>
        )}
        {showLocationSelectors && (
          <Col xs={24} md={8} lg={4}>
            <div data-tour="inventario-provincia">
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text strong>Provincia</Text>
                <Select
                  placeholder="Seleccione provincia"
                  options={provincias.map((provincia) => ({ label: provincia.nombre, value: provincia.id }))}
                  value={selectedProvinciaId}
                  onChange={(value) => onProvinciaChange?.(value)}
                  disabled={provinciasStatus === 'loading'}
                  loading={provinciasStatus === 'loading'}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                />
              </Space>
            </div>
          </Col>
        )}
        {showLocationSelectors && (
          <Col xs={24} md={8} lg={4}>
            <div data-tour="inventario-canton">
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text strong>Cantón</Text>
                <Select
                  placeholder="Seleccione cantón"
                  options={cantones.map((canton) => ({ label: canton.nombre, value: canton.id }))}
                  value={selectedCantonId}
                  onChange={(value) => onCantonChange?.(value)}
                  disabled={!selectedProvinciaId || cantonesStatus === 'loading'}
                  loading={cantonesStatus === 'loading'}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                />
              </Space>
            </div>
          </Col>
        )}
        {!hideMesaSelector && (
          <Col xs={24} md={12} lg={8}>
            <div data-tour="inventario-mesa">
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text strong>Mesa</Text>
                <Select
                  placeholder="Seleccione mesa"
                  options={mesas.map((m) => ({ label: m.grupo_mesa_abreviatura ? `${m.grupo_mesa_abreviatura} - ${m.nombre}` : m.nombre, value: m.id }))}
                  value={selectedMesaId}
                  onChange={(value) => onMesaChange(value)}
                  disabled={mesasStatus === 'loading'}
                  loading={mesasStatus === 'loading'}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                />
              </Space>
            </div>
          </Col>
        )}
        <Col xs={24} md={8} lg={8}>
          <div data-tour="inventario-grupo-recurso">
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space size={6}>
                <Text strong>Grupo recurso / Tipo recurso</Text>
                <Tooltip title="Busque por Grupo o Tipo de recurso. Las coincidencias se mostrarán como Grupo - Tipo de recurso.">
                  <InfoCircleOutlined
                    aria-label="Ayuda para buscar grupos o tipos de recurso"
                    tabIndex={0}
                    style={{ color: '#8c8c8c', cursor: 'help' }}
                  />
                </Tooltip>
              </Space>
              <Select
                placeholder="Buscar grupo o tipo de recurso"
                options={recursoSelectorOptions}
                value={selectedRecursoValue}
                onChange={handleGrupoChange}
                disabled={recursoGruposStatus === 'loading'}
                loading={recursoGruposStatus === 'loading'}
                style={{ width: '100%' }}
                showSearch
                optionLabelProp="label"
                onSearch={(value) => {
                  setGrupoSearchText(value);
                }}
                onDropdownVisibleChange={(open) => {
                  if (!open) setGrupoSearchText('');
                }}
                filterOption={false}
              />
            </Space>
          </div>
        </Col>
        <Col xs={24} md={8} lg={4} style={{ display: 'flex', alignItems: 'end' }}>
          <div data-tour="inventario-cargar-matriz">
            <Button type="default" onClick={handleLoadMatrix} disabled={loadDisabled} loading={loading}>
              Cargar Matriz
            </Button>
          </div>
          
        </Col>
      </Row>

      <div ref={tableContainerRef} data-tour="inventario-tabla-matriz">
        <style>{`
          .inventario-matrix-target-row > td {
            background-color: #fff1b8 !important;
            transition: background-color 0.25s ease;
          }
          .inventario-matrix-target-row:focus {
            outline: 2px solid #faad14;
            outline-offset: -2px;
          }
        `}</style>
        <Table<RecursoTipoRow>
          rowKey={(r) => `${r.recurso_tipo_id}`}
          dataSource={rows}
          columns={columns}
          pagination={false}
          scroll={{ x: 1200, y: 550 }}
          sticky
          bordered
          size="middle"
          loading={loading}
          rowClassName={(row) =>
            row.recurso_tipo_id === highlightedRowId ? 'inventario-matrix-target-row' : ''
          }
          onRow={() => ({ tabIndex: -1 })}
        />
      </div>
    </div>
  );
};

export default InventarioMatrix;
